/**
 * Client-side PostHog event wrapper.
 *
 * The PostHog counterpart to `lib/analytics.ts` (which talks to GA4). Some
 * funnel events — most notably `upgrade_clicked` — historically fired ONLY to
 * GA4 via `trackEvent`, so PostHog never saw them and the free-first-job
 * activation funnel (see `lib/marketing/free-job-pack.ts`) could not be built
 * end-to-end in PostHog. Call this alongside `trackEvent` at those call sites so
 * the same interaction lands in both destinations.
 *
 * Why a plain function rather than `usePostHog()`: several upgrade CTAs
 * (`pricing-cta.tsx`, the `document-detail-view` watermark toast) fire from
 * imperative handlers that don't already hold the hook. A shared function that
 * reads the same `posthog-js` singleton the provider initialises keeps a single
 * consistent, consent-gated code path across every surface.
 *
 * Consent + readiness. Mirrors `trackEvent`: no-ops on SSR, when cookie consent
 * isn't `accepted`, or before the SDK singleton has loaded. `posthog.capture`'s
 * own `before_send` barrier fails closed too, but gating here avoids relying on
 * it and matches the GA4 wrapper's shape.
 */

import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";

type EventPropertyValue = string | number | boolean | null | undefined;

export type PostHogEventProperties = Record<string, EventPropertyValue>;

/**
 * Fire a PostHog custom event from the browser. Silently no-ops on SSR, without
 * accepted consent, or before the SDK has loaded. Safe to call unconditionally
 * from any client component or event handler.
 */
export function capturePostHogEvent(
  name: string,
  properties: PostHogEventProperties = {},
): void {
  if (typeof window === "undefined") return;
  if (readAnalyticsConsent() !== "accepted") return;
  if (!posthog.__loaded) return;

  // Strip undefined so PostHog doesn't record the literal string "undefined";
  // null is meaningful and kept (matches the GA4 wrapper).
  const cleaned: Record<string, Exclude<EventPropertyValue, undefined>> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) cleaned[key] = value;
  }

  posthog.capture(name, cleaned);
}
